// Public snapshot payload for `user:{id}:registrations:snapshot` — the
// per-user channel introduced in ADR 009 (Realtime Client Architecture).
// Carries the full set of the user's current bid_registrations on every
// event so the SnapshotContext can replace state wholesale.
//
// PUBLIC-SAFE only. The broadcast channel is anon-subscribable, so this
// shape MUST NOT contain PII (real names, customer IDs, payment-hold
// internals, reviewer IDs). What's here is what the bidder UI already
// renders to the bidder themselves.

/**
 * Lifecycle states a bid_registration can be in. Sourced from the DB
 * `bid_registration_status` enum; kept narrow to the values that exist
 * today so a future enum addition doesn't silently slip through.
 */
export type BidRegistrationSnapshotStatus = 'pending' | 'approved' | 'denied';

/** Public-safe shape of a single bid_registration row. */
export interface BidRegistrationSnapshotItem {
    id: string;
    auctionId: string;
    status: BidRegistrationSnapshotStatus;
    requestedAt: string;
    paddleNumber: string | null;
    updatedAt: string;
}

/** Full snapshot — every current bid_registration for one user. */
export interface UserRegistrationsSnapshot {
    registrations: BidRegistrationSnapshotItem[];
    /**
     * BL-1782 — present (and `true`) only while the customer's standing is
     * `suspended`. A suspend soft-deletes the customer's live registrations, so
     * `registrations` alone cannot tell a suspended bidder from one who never
     * registered; this is the one bit that can. ABSENT means "not suspended" —
     * which is also what every row written before BL-1782 and the catch-up
     * route's synthesized empty snapshot say, so neither needs a backfill to
     * read correctly.
     *
     * Fan-in on the topic key (rule #40): it is read off the `customers` row
     * whose `id` IS the topic id, and every standing change emits
     * `markMutated({ resourceType: 'customer' })`, which the shared publisher
     * map already routes to this snapshot (BUG-799). A standing only — never a
     * reason: the bidder learns nothing the 403 on their next action would not
     * already tell them.
     */
    biddingSuspended?: true;
}

/** Customer-level inputs to the snapshot that are not per-registration rows. */
export interface UserRegistrationsSnapshotOptions {
    /** True when `customers.status` is `suspended` (BL-1782). */
    biddingSuspended?: boolean;
}

/**
 * Input row shape — duck-typed to match the Drizzle select that
 * admin-web's and bidder-web's repositories return. Listing only the
 * fields we read here keeps callers free to project wider or narrower
 * rows without breaking the mapper.
 */
export interface BidRegistrationRow {
    id: string;
    auctionId: string;
    status: string;
    requestedAt: string;
    paddleNumber: string | null;
    updatedAt: string;
}

const VALID_STATUSES: ReadonlySet<BidRegistrationSnapshotStatus> = new Set<BidRegistrationSnapshotStatus>([
    'pending',
    'approved',
    'denied',
]);

/**
 * Map an array of bid_registration rows to the public snapshot shape.
 * Rows with an unknown status are dropped — the DB enum prevents this
 * today, but dropping is the safer reaction than emitting an item with
 * an invalid status to every subscriber.
 *
 * @param rows - The customer's live (non-deleted) bid_registration rows.
 * @param options - Customer-level inputs; `biddingSuspended` adds the BL-1782
 *   flag, and is omitted from the output entirely when false.
 * @returns The snapshot payload the bidder's `useBidRegistration` reads.
 */
export function mapUserRegistrationsSnapshot(
    rows: BidRegistrationRow[],
    options: UserRegistrationsSnapshotOptions = {},
): UserRegistrationsSnapshot {
    return {
        ...(options.biddingSuspended ? { biddingSuspended: true as const } : {}),
        registrations: rows.flatMap((row) => {
            if (!VALID_STATUSES.has(row.status as BidRegistrationSnapshotStatus)) {
                return [];
            }
            return [{
                id: row.id,
                auctionId: row.auctionId,
                status: row.status as BidRegistrationSnapshotStatus,
                requestedAt: row.requestedAt,
                paddleNumber: row.paddleNumber,
                updatedAt: row.updatedAt,
            }];
        }),
    };
}

